import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';

import { apikeyListApiKeysOptions } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumnDef,
  DropdownMenuItem,
  DropdownMenuSeparator,
  TextOverflow,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { ApiKeyPreview, Loading } from '~/components';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { TableActionDropdown } from '~/lib/components/table/TableActionDropdown';
import { useNavigate } from '~/lib/navigation';
import { useSessionStore } from '~/stores/Session';

import { useDeleteAPIKeyAlert } from './_hooks/useDeleteAPIKeyAlert';
import {
  apiKeyPermissionPresetLabels,
  apiKeyPermissionPresetTableIcons,
  getAPIKeyPermissionPresetFromResponse,
} from './permissions';
import { APIKey } from './utils';

export const PageApiKeys: React.FC = () => {
  const { currentPlatform, currentUser, isSandbox } = useSessionStore();
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('api-keys');
  const platformId = currentUser?.defaultPlatformId ?? '';
  const apiKeysQuery = useQuery({
    ...apikeyListApiKeysOptions({
      path: { platform_id: platformId },
    }),
    enabled: !!platformId,
  });
  const apiKeys = apiKeysQuery.data?.api_keys ?? [];
  const isLoading = apiKeysQuery.isLoading;
  const openDeleteAPIKeyAlert = useDeleteAPIKeyAlert({ platformId });
  const headerDecoration = currentPlatform ? (
    <Badge
      variant="outline"
      className={`ml-1 translate-y-px rounded-md ${
        isSandbox
          ? 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300'
          : 'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300'
      }`}
    >
      {isSandbox ? 'Sandbox' : 'Live'}
    </Badge>
  ) : null;

  const handleDelete = useCallback(
    (entry: APIKey) => {
      openDeleteAPIKeyAlert(entry);
    },
    [openDeleteAPIKeyAlert]
  );

  const columns = useMemo<DataTableColumnDef<APIKey>[]>(
    () => [
      {
        header: 'Name',
        accessor: 'name',
        width: 'minmax(240px, 1fr)',
        cell: ({ row, value }) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate font-medium text-foreground">{value}</span>
            {row.description && (
              <TextOverflow className="min-w-0 flex-1 text-xs text-muted-foreground">{row.description}</TextOverflow>
            )}
          </div>
        ),
      },
      {
        header: 'Permissions',
        accessor: 'permissions',
        width: 'minmax(152px, max-content)',
        className: 'justify-center',
        cell: ({ value }) => {
          const preset = getAPIKeyPermissionPresetFromResponse(value);
          return (
            <Badge variant="ghost" className="gap-1.5 [&>svg]:shrink-0">
              {apiKeyPermissionPresetTableIcons[preset]}
              {apiKeyPermissionPresetLabels[preset]}
            </Badge>
          );
        },
      },
      {
        header: 'Key',
        accessor: 'api_key_token_last_4',
        width: 'max-content',
        className: 'pr-10',
        cell: ({ value }) => <ApiKeyPreview prefix={isSandbox ? 'test' : 'live'} value={value} />,
      },
      getTimestampColumn<APIKey>({
        accessor: 'created_at',
        timeFormat,
        onTimeFormatChange,
        width: 'minmax(120px, max-content)',
        fallback: '-',
      }),
      {
        header: '',
        accessor: 'id',
        width: 'minmax(40px, max-content)',
        className: 'justify-end',
        cell: ({ row }) => (
          <TableActionDropdown>
            <DropdownMenuItem onSelect={() => navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${row.id}`)}>
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => handleDelete(row)}>
              Delete
            </DropdownMenuItem>
          </TableActionDropdown>
        ),
      },
    ],
    [handleDelete, isSandbox, navigate, onTimeFormatChange, timeFormat]
  );

  return (
    <>
      <PageActions headerDecoration={headerDecoration}>
        <Button type="button" size="sm" onClick={() => navigate(`${ROUTE.PLATFORM_API_KEYS}/create`)}>
          <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
          Create API Key
        </Button>
      </PageActions>

      <Loading isLoading={isLoading}>
        <div className="px-[var(--page-margin)] pb-6">
          <DataTable<APIKey>
            {...tableDefaults}
            columns={columns}
            data={apiKeys}
            isLoading={apiKeysQuery.isFetching}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${row.id}`)}
            emptyContent={
              <TableEmpty
                icon={<Icon.Key />}
                title="No API Keys"
                description="Add your first API key to get started."
              />
            }
          />
        </div>
      </Loading>
    </>
  );
};
